function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// @ts-strict-ignore
import React, { useMemo } from 'react';
import { clsx } from 'clsx';
import { ButtonSize, ButtonTheme } from 'common/components/button/constants';
import { QueryBlockReact } from 'common/query-builder/query-block';
import { SelectorType as QueryBlockSelectorType } from 'common/query-builder/query-block/constants';
import { EventItem } from 'common/query-builder/query-block/events-menu/types';
import { filterToBookmarkableEventItem } from 'common/query-builder/query-block/events-menu/util';
import { isSamePropertyReference } from 'common/query-builder/query-block/properties-menu/util';
import { PropertyType, ReportName, ResourceType } from 'common/report/constants';
import { isFirstTimeEventFilter } from 'common/widgets/property-filter-menu/models/utils';
import { Translatable } from 'common/i18n';
import { MultiMetricsQueryBlock } from 'common/query-builder/query-block/multi-metrics-menu/query-block';
import { SvgIconType } from 'common/components/svg-icon';
import { EntryRowProps } from '../row-types/types';
import { Action, Entry, EntryPart, isMetricEntry, isPropertyEntry, isSegmentEntry, QuerySegmentEntry, QuerySuperEntry, SelectorType, SelectorTypeDefinitions } from '../types';
import { getIconForEntry } from '../util/entry-icon';
import { getSegmentationProperty } from '../row-types/row-util';
export function RowSelector(props: EntryRowProps<Entry> & {
  contextPanelParentElement: HTMLElement | null;
  isPlaceholder: boolean;
  selectorIcon?: string;
  selectorType?: SelectorType;
  selected?: any; // Should be: MultiMetricsSectionItem;
  onSelect?: (detail: any) => void;
  onCreate?: (ev?: any) => void;
}) {
  var _props$entry$property, _props$selected, _props$onSelect;
  const selectorIcon = props.selectorIcon || getIconForEntry(props.entry);
  const hasProperty = "property" in props.entry && ((_props$entry$property = props.entry.property) === null || _props$entry$property === void 0 ? void 0 : _props$entry$property.value);
  const selected = (_props$selected = props.selected) !== null && _props$selected !== void 0 ? _props$selected : isMetricEntry(props.entry) ? null : props.entry.event || null;
  const isDisabled = Boolean(isMetricEntry(props.entry) ? false : props.entry.readOnlyEvent);
  const labelOverride = useMemo<Translatable | undefined>(() => {
    const isSet = selected === null || selected === void 0 ? void 0 : selected.value;
    if (!isSet) {
      if ([ReportName.Funnels, ReportName.Flows].includes(props.sectionProps.reportName)) {
        return _ref => {
          let {
            t
          } = _ref;
          return t("query-builder.select-step");
        };
      } else {
        return _ref2 => {
          let {
            t
          } = _ref2;
          return t("query-builder.select-event");
        };
      }
    }

    // fallback to default query-block label behavior
    return undefined;
  }, [selected]);
  const onEventSelected = detail => {
    const {
      item: value,
      trackingProps,
      queryReason
    } = detail;

    // TODO(CC): Handle entry event selected once we can add metric entry
    if (isMetricEntry(props.entry)) {
      return;
    }

    // TODO: This implies that every non-property is an event. Confirm this and implement strong type asserting.
    const event = isPropertyEntry(props.entry) || props.entry.type === ResourceType.Cohort ? value : filterToBookmarkableEventItem(value as EventItem);
    const entry = _objectSpread(_objectSpread({}, props.entry), {}, {
      event
    }) as QuerySuperEntry;
    const isPlaceholder = props.wouldBePlaceholder(entry);
    let action;
    if (props.isStaged && !isPlaceholder) {
      action = Action.Add;
    } else {
      action = Action.Edit;
      if (isSegmentEntry(entry) && entry.customBucket) {
        const existingProperty = getSegmentationProperty(props.entry as QuerySegmentEntry);
        const updatedProperty = getSegmentationProperty(entry);
        if (updatedProperty.propertyType !== PropertyType.Number || !isSamePropertyReference(existingProperty, updatedProperty)) {
          entry.customBucket = null;
        }
      } else {
        // removes invalid first time filters
        const rowFilteringCapabilities = props.getFilteringCapabilities(entry);
        if (!rowFilteringCapabilities.canApplyFirstTimeEventFilter) {
          entry.filters = entry.filters.filter(filter => !isFirstTimeEventFilter(filter));
        }
      }
    }
    props.updateEntry(action, entry, {
      trackingProps,
      queryReason
    });
    if (isPlaceholder) {
      props.setActivePart(EntryPart.NumericProperty, "open");
    }
  };
  const handleSelectorOpen = () => {
    props.setActivePart(EntryPart.Value, "open");
    props.onEntrySelectorDropdownStateChanged(true);
    props.onMetricUnitSelectorDropdownStateChanged(false);
  };
  const handleSelectorClose = () => {
    props.setActivePart(EntryPart.Value, "close");
    props.onEntrySelectorDropdownStateChanged(false);
    props.onMetricUnitSelectorDropdownStateChanged(false);
  };
  if (props.selectorType === SelectorTypeDefinitions.Metrics) {
    return <MultiMetricsQueryBlock selected={selected} leftIcon={selectorIcon as SvgIconType} open={props.isPlaceholder && props.activePart === EntryPart.Value} allDataGroups={props.sectionProps.dataGroupsEnabled} dataGroupId={props.sectionProps.dataGroupId} excludeAutotrackEvents={props.sectionProps.excludeAutotrackEvents} excludeCustomEvents={props.sectionProps.excludeCustomEvents} excludedMetricsRowTypes={props.sectionProps.excludedMetricsRowTypes} hideEditControls={props.sectionProps.hideEditControls} projectHasPeople={props.sectionProps.projectHasPeople} projectHasCohorts={props.sectionProps.projectHasCohorts} queryOrigin={props.sectionProps.queryOrigin} showAllEvents={props.sectionProps.showAllEvents} showAnyEvent={props.sectionProps.showAnyEvent} showSessionEvent={props.sectionProps.showSessions} showSpecialDatasetProperty={props.sectionProps.showSpecialDatasetProperty} showTopEvents={props.sectionProps.showTopEvents} supportB2bCohorts={props.sectionProps.supportB2bCohorts} trackName={props.sectionProps.trackName} projectId={String(props.sectionProps.projectId)} workspaceId={String(props.sectionProps.workspaceId)} userId={props.sectionProps.userId} contextPanelParentElement={props.contextPanelParentElement} onTrack={props.trackEntrySelectorEvent} onSelect={props.onSelect} onCreate={props.onCreate} onClose={handleSelectorClose} onOpen={handleSelectorOpen} onRemove={props.removeEntry} />;
  }
  return <QueryBlockReact key={"entry-selector-".concat(props.index).concat(props.isPlaceholder ? "-placeholder" : "")} className={clsx("entry-selector", {
    'is-disabled': isDisabled,
    'is-placeholder': props.isPlaceholder,
    'has-property': !props.hasNewUI && hasProperty
  })} {...props.queryBlockConfig} label={labelOverride} autofocus={props.index === 0 && props.sectionProps.autofocus && props.isPlaceholder && !props.isFirstEntryStaged} buttonSize={props.sectionProps.buttonTheme === ButtonTheme.Browse ? ButtonSize.Large : ButtonSize.Medium} elref='entrySelector' open={props.isPlaceholder && props.activePart === EntryPart.Value} dataGroupId={props.sectionProps.dataGroupId} enableEnhancedListFilters={props.sectionProps.enableEnhancedListFilters} enableWorkspaceFilterRestrictions={props.sectionProps.enableWorkspaceFilterRestrictions} excludeCustomEvents={props.sectionProps.excludeCustomEvents} hideEditControls={props.sectionProps.hideEditControls} inlineCohortsOverride={props.sectionProps.inlineCohortsOverride} projectHasCohorts={props.sectionProps.projectHasCohorts} projectId={props.sectionProps.projectId} selected={selected} theme={props.sectionProps.buttonTheme || (props.hasNewUI ? ButtonTheme.QueryBuilder : props.entry.readOnlyEvent ? ButtonTheme.Tertiary : ButtonTheme.Select)} trackName={props.sectionProps.trackName} selectorIcon={props.sectionProps.enableEventIcon || !props.hasNewUI ? selectorIcon : ""} selectorType={QueryBlockSelectorType.Button} userId={props.sectionProps.userId} disabled={props.sectionProps.disabled} workspaceId={props.sectionProps.workspaceId} prefixLabel={props.entry.prefixLabel} contextPanelParentElement={props.contextPanelParentElement} onClose={handleSelectorClose} onSavedCustomEvent={props.propagateEvent} onOpen={handleSelectorOpen} onRemove={() => props.removeEntry()} onSelect={(_props$onSelect = props.onSelect) !== null && _props$onSelect !== void 0 ? _props$onSelect : onEventSelected} onTrack={props.trackEntrySelectorEvent} onCreate={props.onCreate} data-sentry-element="QueryBlockReact" data-sentry-component="RowSelector" data-sentry-source-file="selector.tsx" />;
}